import { ChevronRight, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { assignBookmarkToProject } from '@/api/bookmarks';
import { createProject, deleteProject, duplicateProject, updateProject } from '@/api/projects';
import { ConfirmDialog } from '@/components/Modals/ConfirmDialog';
import { Button } from '@/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Input } from '@/components/ui/input';
import { useApp } from '@/state/AppContext';
import type { ProjectNode } from '@/types/models';

const DRAG_SINGLE_MIME = 'application/x-hb-bookmark-id';
const DRAG_MULTI_MIME = 'application/x-hb-bookmark-ids';
const DRAG_PROJECT_MIME = 'application/x-hb-project-id';

interface SidebarProps {
  tree: ProjectNode[];
  onChanged: () => void;
}

type ProjectDropMode = 'move' | 'copy' | null;

export function Sidebar({ tree, onChanged }: SidebarProps) {
  const [creating, setCreating] = useState<{ parentId: string | null } | null>(null);
  const [draftName, setDraftName] = useState('');
  const [topLevelDropMode, setTopLevelDropMode] = useState<ProjectDropMode>(null);

  const startCreate = (parentId: string | null) => {
    setCreating({ parentId });
    setDraftName('');
  };

  const submitCreate = async () => {
    if (!creating || !draftName.trim()) {
      setCreating(null);
      return;
    }
    try {
      await createProject({ name: draftName.trim(), parentId: creating.parentId });
      toast.success('Project created');
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setCreating(null);
      setDraftName('');
    }
  };

  // The header is a "move to top level" / "copy to top level" drop target —
  // only accepts project drags. Gating by `types.includes(DRAG_PROJECT_MIME)`
  // ensures that bookmark drags passing over the header area don't get
  // "stolen" (which would silently drop them with no assignment). Ctrl/Cmd
  // switches the operation from move to deep-copy.
  const handleHeaderDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_PROJECT_MIME)) return;
    e.preventDefault();
    const copy = e.ctrlKey || e.metaKey;
    e.dataTransfer.dropEffect = copy ? 'copy' : 'move';
    setTopLevelDropMode(copy ? 'copy' : 'move');
  };
  const handleHeaderDragLeave = () => setTopLevelDropMode(null);
  const handleHeaderDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const copy = e.ctrlKey || e.metaKey;
    setTopLevelDropMode(null);
    const projectId = e.dataTransfer.getData(DRAG_PROJECT_MIME);
    if (!projectId) return;
    try {
      if (copy) {
        await duplicateProject(projectId, null);
        toast.success('Copied to top level');
      } else {
        await updateProject(projectId, { parentId: null });
        toast.success('Moved to top level');
      }
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <aside className="card-edge bg-card rounded-lg p-2 w-72 flex flex-col gap-2 overflow-y-auto">
      <div
        className={`flex items-center justify-between px-2 pt-1 rounded-md ${
          topLevelDropMode === 'move'
            ? 'ring-2 ring-primary/60 bg-primary/10'
            : topLevelDropMode === 'copy'
              ? 'ring-2 ring-emerald-500/60 bg-emerald-500/10'
              : ''
        }`}
        onDragOver={handleHeaderDragOver}
        onDragLeave={handleHeaderDragLeave}
        onDrop={handleHeaderDrop}
        aria-label="Top-level project drop zone"
      >
        <h2 className="text-sm font-medium text-foreground">Projects</h2>
        <Button
          size="icon"
          variant="ghost"
          aria-label="New top-level project"
          onClick={() => startCreate(null)}
        >
          <Plus />
        </Button>
      </div>

      {creating && creating.parentId === null && (
        <NewProjectInput
          value={draftName}
          onChange={setDraftName}
          onSubmit={submitCreate}
          onCancel={() => setCreating(null)}
          indent={0}
        />
      )}

      <div className="flex flex-col">
        {tree.map((node) => (
          <NodeView
            key={node.id}
            node={node}
            depth={0}
            onChanged={onChanged}
            startCreate={startCreate}
            creating={creating}
            draftName={draftName}
            setDraftName={setDraftName}
            submitCreate={submitCreate}
            cancelCreate={() => setCreating(null)}
          />
        ))}
      </div>
    </aside>
  );
}

interface NodeViewProps {
  node: ProjectNode;
  depth: number;
  onChanged: () => void;
  startCreate: (parentId: string | null) => void;
  creating: { parentId: string | null } | null;
  draftName: string;
  setDraftName: (v: string) => void;
  submitCreate: () => void;
  cancelCreate: () => void;
}

function NodeView({
  node,
  depth,
  onChanged,
  startCreate,
  creating,
  draftName,
  setDraftName,
  submitCreate,
  cancelCreate,
}: NodeViewProps) {
  const { state, selectProject } = useApp();
  const [expanded, setExpanded] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(node.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Tri-state: null = not a drop target; 'move' = moving a project here;
  // 'copy' = deep-copying a project here (Ctrl/Cmd held); 'assign' =
  // assigning bookmark(s) here. Used for the highlight color.
  const [dropMode, setDropMode] = useState<'move' | 'copy' | 'assign' | null>(null);

  const isSelected = state.selectedProjectId === node.id;

  const handleDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.setData(DRAG_PROJECT_MIME, node.id);
    // Both move and copy are valid for project drags; the modifier picks one at drop time.
    e.dataTransfer.effectAllowed = 'copyMove';
  };

  const handleDragOver = (e: React.DragEvent) => {
    const types = e.dataTransfer.types;
    if (types.includes(DRAG_PROJECT_MIME)) {
      e.preventDefault();
      const copy = e.ctrlKey || e.metaKey;
      e.dataTransfer.dropEffect = copy ? 'copy' : 'move';
      setDropMode(copy ? 'copy' : 'move');
    } else if (types.includes(DRAG_SINGLE_MIME) || types.includes(DRAG_MULTI_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDropMode('assign');
    }
  };
  const handleDragLeave = () => setDropMode(null);
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const copy = e.ctrlKey || e.metaKey;
    setDropMode(null);

    // Project drag (move under, or copy under with Ctrl/Cmd).
    const draggedProjectId = e.dataTransfer.getData(DRAG_PROJECT_MIME);
    if (draggedProjectId) {
      if (draggedProjectId === node.id && !copy) return; // self-move is a no-op
      try {
        if (copy) {
          await duplicateProject(draggedProjectId, node.id);
          toast.success(`Copied under ${node.name}`);
        } else {
          await updateProject(draggedProjectId, { parentId: node.id });
          toast.success(`Moved under ${node.name}`);
        }
        onChanged();
      } catch (err) {
        const apiErr = err as { code?: string };
        if (apiErr.code === 'WOULD_CREATE_CYCLE') {
          toast.error(
            copy
              ? `Cannot copy into ${node.name} — that would create a cycle.`
              : `Cannot move under ${node.name} — that would create a cycle.`,
          );
        } else {
          toast.error((err as Error).message);
        }
      }
      return;
    }

    const multi = e.dataTransfer.getData(DRAG_MULTI_MIME);
    if (multi) {
      let ids: string[] = [];
      try {
        ids = JSON.parse(multi) as string[];
      } catch {
        toast.error('Could not parse the dragged selection');
        return;
      }
      let succeeded = 0;
      let failed = 0;
      for (const id of ids) {
        try {
          await assignBookmarkToProject(id, node.id);
          succeeded += 1;
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) {
        toast.success(
          `Assigned ${succeeded} bookmark${succeeded === 1 ? '' : 's'} to ${node.name}`,
        );
      } else {
        toast.error(`${succeeded} assigned, ${failed} failed`);
      }
      onChanged();
      return;
    }

    const bookmarkId = e.dataTransfer.getData(DRAG_SINGLE_MIME);
    if (!bookmarkId) return;
    try {
      await assignBookmarkToProject(bookmarkId, node.id);
      toast.success(`Assigned to ${node.name}`);
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const submitRename = async () => {
    if (!renameDraft.trim() || renameDraft.trim() === node.name) {
      setRenaming(false);
      return;
    }
    try {
      await updateProject(node.id, { name: renameDraft.trim() });
      toast.success('Project renamed');
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setRenaming(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProject(node.id);
      toast.success('Project deleted');
      onChanged();
    } catch (err) {
      const apiErr = err as { details?: { childCount?: number; assignmentCount?: number } };
      const d = apiErr.details;
      if (d?.childCount || d?.assignmentCount) {
        toast.error(
          `Project not empty: ${d.childCount ?? 0} child projects, ${d.assignmentCount ?? 0} bookmark assignments. Empty it first.`,
        );
      } else {
        toast.error((err as Error).message);
      }
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={`flex items-center gap-1 rounded-md px-2 py-1.5 text-sm cursor-pointer select-none ${
              isSelected
                ? 'bg-zinc-800/60 border-l-2 border-primary -ml-0.5'
                : 'hover:bg-zinc-800/40'
            } ${
              dropMode === 'copy'
                ? 'ring-2 ring-emerald-500/60 bg-emerald-500/10'
                : dropMode
                  ? 'ring-2 ring-primary/60 bg-primary/10'
                  : ''
            }`}
            style={{ paddingLeft: `${depth * 12 + 8}px` }}
            data-project-id={node.id}
            draggable={!renaming}
            onDragStart={handleDragStart}
            onClick={() => selectProject(node.id)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {node.children.length > 0 ? (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((v) => !v);
                }}
                aria-label={expanded ? 'Collapse' : 'Expand'}
              >
                <ChevronRight
                  className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                />
              </button>
            ) : (
              <span className="w-3.5" />
            )}
            {renaming ? (
              <Input
                autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitRename();
                  if (e.key === 'Escape') setRenaming(false);
                }}
                onBlur={() => void submitRename()}
                className="h-6 text-sm"
              />
            ) : (
              <span className="truncate">{node.name}</span>
            )}
          </div>
        </ContextMenuTrigger>
        {/*
          onCloseAutoFocus is prevented so Radix doesn't restore focus to the
          context-menu trigger after a "Rename" or "Add Child Project" item is
          chosen. Otherwise that focus restoration races with — and beats —
          the autoFocus on the freshly-mounted Input, leaving the user with
          no caret to type into.
        */}
        <ContextMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
          <ContextMenuItem onSelect={() => setRenaming(true)}>Rename</ContextMenuItem>
          <ContextMenuItem onSelect={() => startCreate(node.id)}>Add Child Project</ContextMenuItem>
          <ContextMenuItem onSelect={() => setConfirmDelete(true)} className="text-destructive">
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {creating && creating.parentId === node.id && (
        <NewProjectInput
          value={draftName}
          onChange={setDraftName}
          onSubmit={submitCreate}
          onCancel={cancelCreate}
          indent={depth + 1}
        />
      )}

      {expanded &&
        node.children.map((child) => (
          <NodeView
            key={child.id}
            node={child}
            depth={depth + 1}
            onChanged={onChanged}
            startCreate={startCreate}
            creating={creating}
            draftName={draftName}
            setDraftName={setDraftName}
            submitCreate={submitCreate}
            cancelCreate={cancelCreate}
          />
        ))}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete project?"
        description={`Permanently delete "${node.name}". This only works if it has no children and no bookmark assignments.`}
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}

function NewProjectInput({
  value,
  onChange,
  onSubmit,
  onCancel,
  indent,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  indent: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Imperative focus deferred to a setTimeout(0) macrotask. When this
    // input is mounted from a Radix ContextMenu's `onSelect` (Add Child
    // Project), Radix's FocusScope schedules its own unmount focus
    // restoration via setTimeout(0). React's `autoFocus` runs during the
    // same commit and gets clobbered by browser-internal focus events
    // triggered when the menu items are removed. Deferring our focus()
    // call to a later macrotask makes it land AFTER all of Radix's
    // cleanup, regardless of timing.
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <div
      className="flex items-center gap-1 px-2 py-1"
      style={{ paddingLeft: `${indent * 12 + 8}px` }}
    >
      <span className="w-3.5" />
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={onSubmit}
        placeholder="Project name"
        className="h-6 text-sm"
      />
    </div>
  );
}
